import { useLayoutEffect, useState } from 'react';

/**
 * Which ends of a horizontal scroller have more past them: the ONE reading every sideways
 * strip shares (the tab strips, the crew chips), so an edge that hides content always says so.
 *
 * ⚠️ UX baseline: a strip that scrolls sideways must SHOW that it does. An overlay scrollbar
 * (macOS, headless Chromium) is invisible until someone already knows to scroll, and a strip
 * cut off at a whole item reads as complete: the crew map's panel hid its "Done" tab that way.
 */
export interface OverflowEdges {
  start: boolean;
  end: boolean;
}

/** Pure, so the edge rule is a test: one pixel of slack for sub-pixel layouts. */
export function overflowEdges(m: {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}): OverflowEdges {
  return {
    start: m.scrollLeft > 1,
    end: m.scrollLeft + m.clientWidth < m.scrollWidth - 1,
  };
}

/**
 * The edges of `el`, kept current on scroll and resize. `content` re-measures when what is in
 * it changes without the box changing (one more chip).
 */
export function useOverflowEdges(
  el: HTMLElement | null,
  content?: unknown,
): OverflowEdges {
  const [edges, setEdges] = useState<OverflowEdges>({ start: false, end: false });
  useLayoutEffect(() => {
    // Layout is a browser's: a DOM without it (the node tests) has no edges to read.
    if (
      !el ||
      typeof el.scrollWidth !== 'number' ||
      typeof el.addEventListener !== 'function'
    )
      return;
    const measure = () => {
      const next = overflowEdges(el);
      setEdges((p) => (p.start === next.start && p.end === next.end ? p : next));
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    if (ro && el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
  }, [el, content]);
  return edges;
}

/**
 * The fade for an edge with more past it. A mask reads only ALPHA: `black` is "shown",
 * `transparent` "faded", whatever the theme, so it needs no colour token.
 */
export function fadeMask({ start, end }: OverflowEdges): string | undefined {
  if (!start && !end) return undefined;
  const FADE = '24px';
  return `linear-gradient(to right, ${start ? 'transparent' : 'black'} 0, black ${FADE}, black calc(100% - ${FADE}), ${end ? 'transparent' : 'black'} 100%)`;
}

/** The bits of a box `innerScrollTakesWheel` reads: a DOM element, or a test's stand-in. */
export interface ScrollBox {
  parentNode: unknown;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/**
 * Does a box between the wheel's target and the strip (`stop`) still scroll up or down the
 * way the wheel turns? Then the wheel is that box's: a Tickets board column scrolls its cards
 * and does not move the board sideways. Pure, so it is a test; `scrollsY` says whether a box
 * scrolls vertically at all (its `overflow-y`), read from the browser by default.
 */
export function innerScrollTakesWheel(
  target: ScrollBox | null,
  stop: unknown,
  deltaY: number,
  scrollsY: (box: ScrollBox) => boolean = (box) =>
    /^(auto|scroll)$/.test(getComputedStyle(box as unknown as Element).overflowY),
): boolean {
  for (let n = target; n && n !== stop; n = n.parentNode as ScrollBox | null) {
    if (typeof n.scrollHeight !== 'number' || n.scrollHeight <= n.clientHeight + 1)
      continue;
    if (!scrollsY(n)) continue;
    const room =
      deltaY > 0 ? n.scrollTop + n.clientHeight < n.scrollHeight - 1 : n.scrollTop > 0;
    if (room) return true;
  }
  return false;
}

/**
 * A vertical wheel scrolls the strip sideways while it can move that way (a mouse wheel has
 * no sideways of its own), unless a box inside the strip under the pointer can still scroll
 * that way (`innerScrollTakesWheel`). A NATIVE, non-passive listener: React registers `wheel`
 * passively, so an `onWheel` cannot stop the page scrolling at the same time. At an end it
 * lets the wheel go, so the page still scrolls past the strip.
 */
export function useSidewaysWheel(el: HTMLElement | null): void {
  useLayoutEffect(() => {
    if (!el || typeof el.addEventListener !== 'function') return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (innerScrollTakesWheel(e.target as unknown as ScrollBox, el, e.deltaY)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const next = Math.max(0, Math.min(max, el.scrollLeft + e.deltaY));
      if (next === el.scrollLeft) return;
      e.preventDefault();
      el.scrollLeft = next;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [el]);
}
